import React, { useEffect, useMemo, useRef, useState } from 'react';

interface ProgressBarProps {
  num: string;
  minValue: number;
  maxValue: number;
  currentValue: number;
  colorVariant?: 'negative' | 'neutral' | 'positive';
}


const ProgressBarForHome: React.FC<ProgressBarProps> = ({
  num,
  minValue,
  maxValue,
  currentValue,
  colorVariant = 'neutral',
}) => {
  const targetProgress = useMemo(() => {
    const numericValue = parseFloat(String(currentValue).replace(',', '.')) || 0;
    const numericMax = parseFloat(String(maxValue).replace(',', '.')) || 1;
    const numericMin = parseFloat(String(minValue).replace(',', '.')) || 0;
    const range = numericMax - numericMin;
    if (range <= 0) {
      return 0;
    }

    const clampedValue = Math.min(Math.max(numericValue, numericMin), numericMax);
    return (clampedValue - numericMin) / range;
  }, [currentValue, maxValue, minValue]);
  const [progress, setProgress] = useState(0);
  const frameRef = useRef<number | null>(null);
  const hasAnimatedInitialRef = useRef(false);

  useEffect(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }

    if (!hasAnimatedInitialRef.current) {
      hasAnimatedInitialRef.current = true;
      setProgress(0);
      frameRef.current = window.requestAnimationFrame(() => {
        setProgress(targetProgress);
      });
    } else {
      setProgress(targetProgress);
    }

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [targetProgress]);

  const progressColorStyles = useMemo(() => {
    switch (colorVariant) {
      case 'negative':
        return {
          backgroundColor: 'rgba(255, 0, 0, 0.15)',
          borderRight: '2px solid #ff3232',
        };
      case 'positive':
        return {
          backgroundColor: 'rgba(0, 255, 0, 0.15)',
          borderRight: '2px solid #32ff32',
        };
      default:
        return {
          backgroundColor: 'rgba(75, 192, 192, 0.15)',
          borderRight: '2px solid #4bc0c0',
        };
    }
  }, [colorVariant]);

  return (
    <div
      style={{
        width: '100%',
        background: 'transparent',
        height: '26px',
        borderRadius: '0px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '26px',
          width: '100%',
          ...progressColorStyles,
          transformOrigin: 'left center',
          transform: `scaleX(${progress})`,
          transition: 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />
    </div>
  );
};

export default React.memo(
  ProgressBarForHome,
  (prev, next) =>
    prev.currentValue === next.currentValue &&
    prev.minValue === next.minValue &&
    prev.maxValue === next.maxValue &&
    prev.num === next.num &&
    prev.colorVariant === next.colorVariant
);
